import { fetchPartnerRequestById } from 'legacy/lut/services/partner/fetchPartnerRequestById'
import { useCallback, useEffect, useRef, useState } from 'react'

import { inactivatePartnerRequest } from 'clickNgo/entities/partner/api'
import { PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useGetFormDataField } from 'cng-common/features/form'
import Logger from 'core/utils/Logger'

import { PartnerDataFormKeys, PartnerDataFormSettings } from '../../../configs'
import { IPartnerDataFormData } from '../../../models'
import { checkPossibilityChangeStatus } from '../helpers/checkPossibilityChangeStatus'

export const useInactivatePartner = () => {
  const [actualData, updateActulaData] = useGetFormDataField<IPartnerDataFormData>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.ActualData
  )

  const [isInactive] = useGetFormDataField<boolean>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.IsInactive
  )

  const [isPartnerInactivationPossible, setIsPartnerInactivationPossible] = useState(false)
  const [partnerInactivationUnpossibleMessage, setPartnerInactivationUnpossibleMessage] =
    useState<string>()
  const [partnerInactivationErrorMessages, setPartnerInactivationErrorMessages] = useState<
    string[]
  >([])

  const latestActualData = useRef(actualData)
  latestActualData.current = actualData

  const inactivatePartner = useCallback(async () => {
    setPartnerInactivationErrorMessages([])

    const { partnerId, locationId, onboardingRequestId, source } = latestActualData.current.partner
    const result = await inactivatePartnerRequest({ partnerId, locationId })
    const errorMessages = getJSendErrorMessages(result)

    if (errorMessages.length) {
      setPartnerInactivationErrorMessages(errorMessages)

      return
    }

    const partner = await fetchPartnerRequestById(partnerId, { isForce: true }).catch(
      (error: unknown) => {
        Logger.error(error)

        return null
      }
    )

    const current = latestActualData.current

    updateActulaData({
      ...current,
      partner: partner
        ? { ...partner, onboardingRequestId, source }
        : {
            ...current.partner,
            status: result.data.partnerStatus,
            workflowStatus: PartnerWorkflowStatus.NEW,
          },
    })
  }, [updateActulaData])

  useEffect(() => {
    let isOutdated = false

    if (actualData?.partner) {
      checkPossibilityChangeStatus(actualData.partner)
        .catch(() => 'Could not check whether this partner can be inactivated')
        .then((result) => {
          if (isOutdated) {
            return
          }

          setPartnerInactivationUnpossibleMessage(result || undefined)
          setIsPartnerInactivationPossible(!result)
        })
    }

    return () => {
      isOutdated = true
    }
  }, [actualData?.partner])

  return {
    isInactive,
    inactivatePartner,
    partnerInactivationUnpossibleMessage,
    partnerInactivationErrorMessages,
    isPartnerInactivationPossible,
  }
}
